/* I&C · motion layer · Chrome (rd-header.liquid, rd-footer.liquid, hero .scroll-cue)
   Header: warm halo breathing behind the logo while over the hero, underline + gold dot across the nav,
   a glass "settle" when the header turns solid, a one-off bump when the bag count changes.
   Footer: staggered rise, hairlines that draw gold then settle, halo on the wordmark.
   Hero: the scroll cue becomes a feathered pulsing line with a "Scroll" label.
   Single IIFE, no globals. Does nothing under prefers-reduced-motion. Never edits ic-rd.js. */
(function () {
  'use strict';
  var ARMED = 'rd-m-chrome';
  var POINTER = '(hover: hover) and (pointer: fine)';

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    n.className = cls;
    n.setAttribute('aria-hidden', 'true');
    if (text) n.textContent = text;
    return n;
  }

  function mark(root, sel, cls) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), function (n) { n.classList.add(cls); });
  }

  /* warm halo behind a wordmark, centred on the logo/initials; JS positions it so it sits on the mark, not the link box */
  function halo(wordmark, size) {
    if (!wordmark || wordmark.querySelector('.rd-m-chrome__halo')) return null;
    var h = el('span', 'rd-m-chrome__halo');
    wordmark.insertBefore(h, wordmark.firstChild);
    h.style.setProperty('--rd-hs', size + 'px');
    void h.offsetWidth; /* flush the inserted node's style once so the first rd-m-live fades in instead of snapping */
    var mark = wordmark.querySelector('.wordmark__logo, .wordmark__main');
    function place() {
      if (!mark) return;
      wordmark.style.setProperty('--rd-hx', (mark.offsetLeft + mark.offsetWidth / 2) + 'px');
      wordmark.style.setProperty('--rd-hy', (mark.offsetTop + mark.offsetHeight / 2) + 'px');
    }
    return place;
  }

  /* re-run measurements after fonts and on resize (rAF-coalesced, passive) */
  function onLayout(fns) {
    var raf = 0;
    function run() { raf = 0; for (var i = 0; i < fns.length; i++) fns[i](); }
    function queue() { if (!raf) raf = requestAnimationFrame(run); }
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
    window.addEventListener('resize', queue, { passive: true });
    window.addEventListener('load', queue, { passive: true });
  }

  /* ------------------------------------------------------------------ header */
  function header(root, cue) {
    if (root.hasAttribute('data-rd-m-chrome')) return [];
    root.setAttribute('data-rd-m-chrome', '');
    root.classList.add(ARMED, 'rd-m-head');
    var measures = [];
    mark(root, '.nav a, .nav button, .utils > a', 'rd-m-u');

    var place = halo(root.querySelector('.wordmark'), 190);
    if (place) measures.push(place);

    /* live halo only while the header floats over the hero (not .is-solid); settle sweep when it changes */
    var solid = root.classList.contains('is-solid'), settleT = 0;
    function syncSolid(first) {
      var now = root.classList.contains('is-solid');
      root.classList.toggle('rd-m-live', !now);
      if (cue) cue.classList.toggle('rd-m-away', now);
      if (now === solid && !first) return;
      solid = now;
      if (first) return;
      clearTimeout(settleT);
      root.classList.remove('rd-m-settle');
      void root.offsetWidth; /* restart the keyframe (only on an actual state change) */
      root.classList.add('rd-m-settle');
      settleT = setTimeout(function () { root.classList.remove('rd-m-settle'); }, 1800);
    }
    syncSolid(true);
    if ('MutationObserver' in window) {
      new MutationObserver(function () { syncSolid(false); }).observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    /* gold dot sliding between nav items (fine pointer only; keyboard focus moves it too) */
    var nav = root.querySelector('.nav');
    if (nav && window.matchMedia(POINTER).matches) {
      var items = Array.prototype.slice.call(nav.querySelectorAll('a, button'));
      var dot = el('span', 'rd-m-chrome__dot');
      nav.appendChild(dot);
      var xs = [];
      measures.push(function () {
        xs = items.map(function (it) { return it.offsetLeft + it.offsetWidth / 2; });
      });
      function current() {
        for (var i = 0; i < items.length; i++) {
          if (items[i].getAttribute('aria-current') === 'page' || items[i].getAttribute('aria-expanded') === 'true') return i;
        }
        return -1;
      }
      function go(i) {
        if (i < 0 || !xs.length) { nav.classList.remove('rd-m-dot'); return; }
        nav.style.setProperty('--rd-dx', xs[i] + 'px');
        nav.classList.add('rd-m-dot');
      }
      function from(e) {
        var t = e.target && e.target.closest ? e.target.closest('a, button') : null;
        return t ? items.indexOf(t) : -1;
      }
      nav.addEventListener('pointerover', function (e) { var i = from(e); if (i >= 0) go(i); }, { passive: true });
      nav.addEventListener('pointerleave', function () { go(current()); }, { passive: true });
      nav.addEventListener('focusin', function (e) { var i = from(e); if (i >= 0) go(i); }, { passive: true });
      nav.addEventListener('focusout', function () { go(current()); }, { passive: true });
      if (current() >= 0) measures.push(function () { go(current()); });
    }

    /* bag count: pulse once whenever its contents change (Shopify re-renders #cart-icon-bubble) */
    var bag = root.querySelector('.rd-bag');
    if (bag && 'MutationObserver' in window) {
      var last = bag.textContent.trim(), bumpT = 0;
      new MutationObserver(function () {
        var txt = bag.textContent.trim();
        if (txt === last) return;
        last = txt;
        clearTimeout(bumpT);
        bag.classList.remove('rd-m-bump');
        void bag.offsetWidth;
        bag.classList.add('rd-m-bump');
        bumpT = setTimeout(function () { bag.classList.remove('rd-m-bump'); }, 1200);
      }).observe(bag, { childList: true, characterData: true, subtree: true });
    }
    return measures;
  }

  /* ------------------------------------------------------------------ footer */
  function footer(root) {
    if (root.hasAttribute('data-rd-m-chrome')) return [];
    root.setAttribute('data-rd-m-chrome', '');
    root.classList.add(ARMED, 'rd-m-foot', 'rd-m-hair');
    var measures = [];
    mark(root, '.footer__col ul a', 'rd-m-u');
    mark(root, '.footer__base, .footer__col details', 'rd-m-hair');

    /* stagger indices: columns (--i) and the list items inside each (--j) */
    var blocks = Array.prototype.slice.call(root.querySelectorAll('.footer__top > *'));
    var base = root.querySelector('.footer__base');
    if (base) blocks.push(base);
    blocks.forEach(function (b, i) {
      b.style.setProperty('--i', i);
      Array.prototype.forEach.call(b.querySelectorAll('li'), function (li, j) { li.style.setProperty('--j', j); });
    });

    var place = halo(root.querySelector('.wordmark'), 220);
    if (place) measures.push(place);

    if ('IntersectionObserver' in window) {
      var onScreen = false;
      function live() { root.classList.toggle('rd-m-live', onScreen && !document.hidden); }
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          onScreen = en.isIntersecting;
          if (en.isIntersecting && en.intersectionRatio > 0) root.classList.add('rd-m-in');
        });
        live();
      }, { rootMargin: '0px 0px -8% 0px', threshold: [0, 0.05] }).observe(root);
      document.addEventListener('visibilitychange', live, { passive: true });
    } else {
      root.classList.add('rd-m-in', 'rd-m-live');
    }
    return measures;
  }

  /* ------------------------------------------------------------------ hero scroll cue (only thing in the hero we touch) */
  function scrollCue(cue) {
    if (cue.hasAttribute('data-rd-m-chrome')) return;
    cue.setAttribute('data-rd-m-chrome', '');
    cue.classList.add('rd-m-chrome-cue');
    cue.appendChild(el('span', 'rd-m-chrome__cue-label', 'Scroll'));
  }

  function init() {
    if (!window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var head = document.querySelector('.rd .site-header[data-rd-header]');
    var foot = document.querySelector('.rd .site-footer');
    var cue = document.querySelector('.rd .hero .scroll-cue');
    if (!head && !foot && !cue) return;
    var measures = [];
    if (cue) scrollCue(cue);
    if (head) measures = measures.concat(header(head, cue));
    if (foot) measures = measures.concat(footer(foot));
    if (measures.length) onLayout(measures);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
